import { server } from '../core/server/axios';
import { analyticsConfig, analyticsErrorText, analyticsQuery } from './AnalyticsClient';

/**
 * Данные по перемещениям — модуль казначейства бэка ECASH Analytics (не бэк ECASH!), ТОЛЬКО ЧТЕНИЕ:
 * список, правило курса, сводка инкассатору (расчёты), журнал рекомендаций, котировки.
 * Перемещения создаются на бэке ECASH — `IncassationController.intake`.
 *
 * Адрес — `ANALYTICS_URL`, авторизация та же, что и у бэка ECASH (токен подставляет `server`).
 * Даты здесь — бизнес-даты `YYYY-MM-DD` по Asia/Almaty: так устроен контракт этого сервиса,
 * правило про полный ISO-момент относится к бэку ECASH.
 *
 * Ошибки приходят как `{ detail: string | [{ msg }] }` — текст для показа берите через `transfersErrorText`.
 */

const TRANSFERS = '/api/treasury/transfers';

export interface Transfer {
    id: number;
    transfer_date: string;
    currency: string;
    amount: number;
    /** Курсы nullable: принятая рекомендация падает без курса, казначей заполняет позже. Tenge/Profit до этого 0 */
    avg_rate: number | null;
    sale_rate: number | null;
    tenge: number;
    profit: number;
    from_branch: string;
    to_branch: string;
    /** Реальные id отделений, сервер находит их по названиям; null — название не сопоставилось ни с одной точкой */
    from_dep_id: number | null;
    to_dep_id: number | null;
    /** ECS_Kassa.Id основной кассы точки: по нему QUIQ принимает заявку. null — точка не сопоставилась */
    from_cash_id: number | null;
    to_cash_id: number | null;
    note: string | null;
    created_by: string | null;
    /** Строка планирования собрана из заявки инкассации: id и номер нужны, чтобы открыть её карточку */
    incassation_id?: number;
    incassation_number?: string;
}

export interface TransfersResponse {
    items: Transfer[];
    totals: { tenge: number; profit: number };
}

export interface TransfersFilter {
    date_from?: string;
    date_to?: string;
    from_branch?: string;
    to_branch?: string;
    city?: string;
}

export interface TransfersOptions {
    branches: string[];
    currencies: string[];
    cities?: string[];
}

/** Тело POST/PUT перемещения. Примечание обязательно (минимум MIN_TRANSFER_NOTE_LEN символов) */
export interface TransferBody {
    transfer_date: string;
    currency: string;
    amount: number;
    avg_rate: number;
    sale_rate: number;
    from_branch: string;
    to_branch: string;
    note: string | null;
}

/**
 * Фиксированное правило курса сделки. `locked: true` — казначей не правит avg_rate/sale_rate
 * на этом плече from→to→currency: сервер перезаписывает всё, что пришло.
 */
export interface DealRateInfo {
    rate: number | null;
    avgRate: number | null;
    basis:
        | "board_sell"
        | "board_sell_wac_floor"
        | "min_wac_buy"
        | "pair_midpoint"
        | "pair_midpoint_wac_floor"
        | "unlocked";
    locked: boolean;
    label: string;
}

export interface DealRateParams {
    from_branch: string;
    to_branch: string;
    currency: string;
    transfer_date: string;
}

export type NettingRequestStatus = "new" | "accepted" | "completed";

export interface NettingRequest {
    id: number;
    city: string;
    status: NettingRequestStatus;
    transfer_date: string;
    netting_plan: Record<string, unknown>;
    created_by: string;
    created_at: string;
    accepted_by: string | null;
    accepted_at: string | null;
    completed_by: string | null;
    completed_at: string | null;
}

export type NettingMode = "multilateral" | "bilateral";

/** Строка для расчёта нетто. kzt_amount берётся из Tenge строки как есть, не пересчитывается */
export interface NettingTransferInput {
    currency: string;
    qty: number;
    from_branch: string;
    to_branch: string;
    kzt_amount: number | null;
    deal_rate?: number;
}

export interface CcyTransfer { from: string; to: string; qty: number }
export interface KztTransfer { from: string; to: string; kzt: number }

export interface CurrencyPlanEntry {
    net: Record<string, number>;
    optimized: CcyTransfer[];
    gross_trips: number;
    gross_volume: number;
    netted_trips: number;
    netted_volume: number;
}

export interface KztPlan {
    net: Record<string, number>;
    optimized: KztTransfer[];
    gross_trips: number;
    gross_volume: number;
    netted_trips: number;
    netted_volume: number;
}

export interface NettingResponse {
    currency_plan: Record<string, CurrencyPlanEntry>;
    kzt_plan: KztPlan;
    skipped: { reason: string }[];
}

export interface RouteStop {
    branch_id: string;
    name: string;
    lat: number;
    lng: number;
    seq: number;
    leg_km: number;
    cum_km: number;
    is_return: boolean;
    /** Второй заезд в ту же точку — отдать то, что забрали позже */
    is_revisit: boolean;
}

export interface RoutePlan {
    order: RouteStop[];
    total_km: number;
    stops: number;
    round_trip: boolean;
    revisits: number;
}

export interface CityRoute {
    city: string;
    route: RoutePlan;
    diagnostics: { branches_in_plan: number; missing_geo: string[]; geo_rows: number };
}

export interface RouteResponse {
    ok: boolean;
    routes: CityRoute[];
}

export interface ImportRowIssue {
    rowNumber: number;
    field: string | null;
    message: string;
}

/** `warnings` — строки, курс которых заменит правило при импорте. Импорт они не блокируют */
export interface ImportPreview {
    total: number;
    valid: number;
    errors: ImportRowIssue[];
    warnings?: ImportRowIssue[];
}

export type SuggestionAction = "accepted" | "edited" | "rejected";

/** Журнал разбора рекомендаций по перемещениям из вкладки «Лимиты» */
export interface SuggestionLogEntry {
    id: number;
    suggestion_key: string;
    transfer_date: string;
    from_branch: string;
    to_branch: string;
    from_branch_display: string;
    to_branch_display: string;
    route_changed: boolean;
    currency: string;
    suggested_amount: number;
    action: SuggestionAction;
    final_amount: number | null;
    final_avg_rate: number | null;
    final_sale_rate: number | null;
    final_from_branch: string | null;
    final_to_branch: string | null;
    resolved_transfer_id: number | null;
    note: string | null;
    acted_by: string;
    acted_at: string | null;
}

export interface QuotedRatesResponse {
    rates: Record<string, { buy: number | null; sale: number | null }>;
}

/** Тело POST /suggestions/resolve; `final_*` — что реально создали, если отличается от рекомендации */
export interface ResolveSuggestionBody {
    suggestion_key: string;
    transfer_date: string;
    from_branch: string;
    to_branch: string;
    currency: string;
    suggested_amount: number;
    action: SuggestionAction;
    final_amount?: number | null;
    final_avg_rate?: number | null;
    final_sale_rate?: number | null;
    final_from_branch?: string | null;
    final_to_branch?: string | null;
    note?: string | null;
}

/** Строка утреннего снимка рекомендаций — знаменатель для «сколько проигнорировали» */
export interface MorningSuggestionItem {
    suggestion_key: string;
    transfer_date: string;
    from_branch: string;
    to_branch: string;
    currency: string;
    suggested_amount: number;
    is_plan_b: boolean;
    is_concentration: boolean;
}

/** Один элемент массива, который уходит в QUIQ (`POST /incassation/intake`) — всё по id отделов и касс */
export interface QuiqTransferItem {
    Currency: string;
    Amount: number;
    /** Курс сделки (sale_rate) */
    Rate: number;
    Tenge: number;
    FromDepId: number;
    FromCashId: number;
    ToDepId: number;
    ToCashId: number;
}

/** Перемещение, которое не удалось собрать в заявку, и почему (нет курса сделки, точка не сопоставлена…) */
export interface QuiqSkipped { id: number; reason: string }

export interface QuiqPreview { transfers: QuiqTransferItem[]; skipped: QuiqSkipped[] }

export interface QuiqSendResult {
    count: number;
    /** id перемещений, ушедших в QUIQ */
    sent: number[];
    skipped: QuiqSkipped[];
    /** id перемещения → номер заявки в QUIQ; пусто, пока QUIQ не вернёт номера */
    refs: Record<string, string | null>;
}

type Opts = { signal?: AbortSignal };

/**
 * Итог «Автоперемещений». `sent` — сколько заявок ушло в ECASH на согласование казначею, `numbers` — их номера (INC-…);
 * `skipped` — что не посчитано автоматически и почему, `rejected` — строки, которые ECASH не принял.
 * `empty` — рекомендаций нет; `send_failed` / `failed` — ничего не создано, причина в `error`.
 */
export interface AutoTransfersResult {
    status: 'sent' | 'empty' | 'send_failed' | 'failed';
    date?: string;
    planned?: number;
    sent: number;
    numbers: string[];
    rejected: string[];
    skipped?: { from_branch: string; to_branch: string; currency: string; reason: string }[];
    error?: string | null;
}

/**
 * Текст ошибки для показа. Кроме обычных `detail`-строк разбирает ответ отправки в QUIQ:
 * при 422 `detail` — объект `{ skipped: [{ id, reason }] }`, и без разбора остался бы голый «HTTP 422».
 */
export const transfersErrorText = (e: unknown): string => {
    const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;

    if (detail && typeof detail === 'object' && !Array.isArray(detail) && Array.isArray((detail as { skipped?: unknown }).skipped)) {
        const skipped = (detail as { skipped: QuiqSkipped[] }).skipped;

        return skipped.map((x) => `#${x.id}: ${x.reason}`).join('; ');
    }

    return analyticsErrorText(e);
};

export class TransfersController {
    getOptions({ signal }: Opts = {}): Promise<TransfersOptions> {
        return server.get(`${TRANSFERS}/options`, analyticsConfig({ signal }));
    }

    getList(filter: TransfersFilter, { signal }: Opts = {}): Promise<TransfersResponse> {
        return server.get(`${TRANSFERS}${analyticsQuery({ ...filter })}`, analyticsConfig({ signal }));
    }

    getDealRate(params: DealRateParams, { signal }: Opts = {}): Promise<DealRateInfo> {
        return server.get(`${TRANSFERS}/deal-rate${analyticsQuery({ ...params })}`, analyticsConfig({ signal }));
    }

    netting(transfers: NettingTransferInput[], mode: NettingMode, { signal }: Opts = {}): Promise<NettingResponse> {
        return server.post(`${TRANSFERS}/netting`, { transfers, mode }, analyticsConfig({ signal }));
    }

    route(transfers: NettingTransferInput[], mode: NettingMode, { signal }: Opts = {}): Promise<RouteResponse> {
        return server.post(`${TRANSFERS}/route`, { transfers, mode }, analyticsConfig({ signal }));
    }

    /** Без дат — весь журнал; с датами — только за период (вкладка «Лимиты» берёт сегодняшний день) */
    getSuggestionsLog(range: { date_from?: string; date_to?: string } = {}, { signal }: Opts = {}): Promise<SuggestionLogEntry[]> {
        return server.get(`${TRANSFERS}/suggestions/log${analyticsQuery({ ...range })}`, analyticsConfig({ signal }));
    }

    /** Котировки табло по ключу `точка|валюта`; кассиры перевыставляют курсы в течение дня */
    getQuotedRates({ signal }: Opts = {}): Promise<QuotedRatesResponse> {
        return server.get(`${TRANSFERS}/quoted-rates`, analyticsConfig({ signal }));
    }

    /**
     * Кнопка казначея: analytics считает рекомендации и создаёт заявки в ECASH. Неидемпотентно по смыслу — без автоповторов;
     * повтор за день analytics узнаёт по ключу пакета и вернёт прежние номера, а не задвоит заявки. Только казначей и админ.
     */
    createAutoTransfers(): Promise<AutoTransfersResult> {
        return server.post('/api/treasury/auto-transfers/create', {}, analyticsConfig({ timeout: 120000 }));
    }
}
